import { useEffect, useMemo, useState } from "react";
import { fetchHistory, type Snapshot } from "../lib/history";
import { SparkLine } from "./SparkLine";

type Row = {
  resolver: string;
  location: string;
  state: "pending" | "ok" | "nxdomain" | "timeout" | "error" | "servfail";
  records?: string[];
  latencyMs?: number;
  message?: string;
  /** AD bit — only `true` is a positive DNSSEC-validation signal. */
  ad?: boolean;
};

type Props = { rows: Row[]; topAnswer?: string; showHistory?: boolean };

type SortKey = "resolver" | "location" | "status" | "latency";
type SortDir = "asc" | "desc";

/**
 * Console-style resolver table:
 *   Resolver | Location | Status | Latency (sortable) | 30-day p50 (sparkline) | Answer
 *
 * Default sort: latency asc with failures pushed to the bottom, so the
 * fastest successful resolvers are at the top regardless of which one
 * happens to return first via SSE.
 */
export function ResultsTable({ rows, topAnswer, showHistory = true }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>("latency");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [history, setHistory] = useState<Snapshot | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!showHistory) return;
    fetchHistory()
      .then((snap) => {
        if (!cancelled) setHistory(snap);
      })
      .catch(() => {/* sparklines render dimmed fallback */});
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedRows = useMemo(() => sortRows(rows, sortKey, sortDir), [rows, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      // sensible defaults per column
      setSortDir(key === "latency" ? "asc" : "asc");
    }
  };

  return (
    // Scroll container: seven columns don't fit a phone, and without this the
    // table pushed the whole PAGE sideways rather than scrolling itself. The
    // leaderboard already used this pattern; the main results table never did.
    <div className="results-wrap">
    <table className="results">
      <thead>
        <tr>
          <SortableTh label="Resolver" k="resolver" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
          <SortableTh label="Location" k="location" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
          <SortableTh label="Status" k="status" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} />
          <th className="col-dnssec" title="Did this resolver cryptographically validate the answer? (DNS AD bit)">DNSSEC</th>
          <SortableTh label="Latency" k="latency" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} align="right" />
          <th className="col-spark">30-day p50</th>
          <th>Answer</th>
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((r) => {
          const tone =
            r.state === "timeout" || r.state === "error" || r.state === "servfail" ? "fail" :
            r.state === "ok" || r.state === "nxdomain" ? "ok" :
            undefined;
          const series = history?.resolvers[r.resolver] ?? [];
          return (
            <tr key={r.resolver} className={`row-${r.state}`}>
              <td className="name">{r.resolver}</td>
              <td className="loc">{r.location.toLowerCase()}</td>
              <td className="col-status">{renderStatus(r)}</td>
              <td className="col-dnssec">{renderDnssec(r)}</td>
              <td className="col-latency">{r.latencyMs != null ? `${r.latencyMs} ms` : <span className="dim">—</span>}</td>
              <td className="col-spark">
                <SparkLine data={series} tone={tone === "fail" ? "fail" : "ok"} />
              </td>
              <td className="col-answer">{renderAnswer(r, topAnswer)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
}

function SortableTh({
  label, k, sortKey, sortDir, onClick, align,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onClick: (k: SortKey) => void;
  align?: "right";
}) {
  const active = sortKey === k;
  return (
    <th
      className={`sortable${align === "right" ? " right" : ""}${active ? " active" : ""}`}
      onClick={() => onClick(k)}
      aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
    >
      {label}
      <span className="sort-indicator" aria-hidden="true">{active ? (sortDir === "asc" ? " ↑" : " ↓") : ""}</span>
    </th>
  );
}

function renderStatus(r: Row) {
  if (r.state === "pending") return <span className="status status-pending">querying</span>;
  if (r.state === "timeout") return <span className="status status-fail">timeout</span>;
  if (r.state === "servfail") {
    return (
      <span
        className="status status-fail"
        title="SERVFAIL: the resolver could not produce an answer. On a validating resolver the usual cause is a broken DNSSEC chain."
      >
        servfail
      </span>
    );
  }
  if (r.state === "error")   return <span className="status status-fail" title={r.message}>error</span>;
  if (r.state === "nxdomain") return <span className="status status-warn">nxdomain</span>;
  return <span className="status status-ok">ok</span>;
}

/**
 * DNSSEC column. Deliberately three-valued rather than a yes/no tick:
 * AD=1 proves validation, but AD=0 cannot distinguish "unsigned zone" from
 * "this resolver doesn't validate", and some resolvers never report the bit.
 * Claiming a red ✗ for those would be misinformation, so they render neutral.
 */
function renderDnssec(r: Row) {
  if (r.state === "pending") return <span className="dim">…</span>;
  if (r.state === "servfail") {
    return <span className="dnssec dnssec-fail" title="SERVFAIL — often a broken DNSSEC signature chain">bogus?</span>;
  }
  if (r.state === "timeout" || r.state === "error") return <span className="dim">—</span>;
  if (r.ad === true) {
    return <span className="dnssec dnssec-ok" title="This resolver cryptographically validated the answer (AD bit set)">✓ signed</span>;
  }
  if (r.ad === false) {
    // NOT "unsigned" — that would assert something about the zone we can't
    // know. dnssec-failed.org is signed (badly) yet non-validating resolvers
    // still answer with AD=0. All we can honestly say is: not validated here.
    return <span className="dim" title="This resolver did not assert validation: either the zone is unsigned, or this resolver does not validate DNSSEC">not validated</span>;
  }
  return <span className="dim" title="This resolver did not report the AD bit">—</span>;
}

function renderAnswer(r: Row, topAnswer?: string) {
  if (r.state === "pending") return <span className="dim">…</span>;
  if (r.state === "servfail") return <span className="dim">SERVFAIL</span>;
  if (r.state === "timeout" || r.state === "error") return <span className="dim">—</span>;
  if (r.state === "nxdomain") return <span className="dim">NXDOMAIN</span>;
  if (!r.records || r.records.length === 0) return <span className="dim">(empty)</span>;
  const joined = r.records.slice().sort().join(", ");
  const matches = topAnswer != null && joined === topAnswer;
  const first = r.records[0];
  const extra = r.records.length > 1 ? <span className="answer-more"> +{r.records.length - 1}</span> : null;
  return (
    <span className={`answer-cell ${matches ? "match" : "mismatch"}`} title={r.records.join(", ")}>
      <code>{first}</code>
      {extra}
    </span>
  );
}

function sortRows(rows: Row[], key: SortKey, dir: SortDir): Row[] {
  const mul = dir === "asc" ? 1 : -1;
  const stateOrder = (s: Row["state"]) =>
    s === "ok" || s === "nxdomain" ? 0 :
    s === "pending" ? 1 :
    s === "timeout" || s === "error" || s === "servfail" ? 2 : 3;
  return rows.slice().sort((a, b) => {
    if (key === "resolver") return mul * a.resolver.localeCompare(b.resolver);
    if (key === "location") return mul * a.location.localeCompare(b.location);
    if (key === "status") return mul * (stateOrder(a.state) - stateOrder(b.state));
    // latency: always group ok above fail; within ok, smaller-first based on dir
    const aOk = a.state === "ok" || a.state === "nxdomain";
    const bOk = b.state === "ok" || b.state === "nxdomain";
    if (aOk !== bOk) return aOk ? -1 : 1;
    if (!aOk && !bOk) return 0;
    const al = a.latencyMs ?? Number.POSITIVE_INFINITY;
    const bl = b.latencyMs ?? Number.POSITIVE_INFINITY;
    return mul * (al - bl);
  });
}
